/**
 * Settings → Treasury (DEV-139): which accounts hold customer money and our
 * own, and how the daily commission transfer runs. Stored in the versioned
 * settings store (`kyc.settings_versions`), one namespace per provider, and no
 * longer inside the provider's connection row.
 *
 * This is the STORED shape: what a reader accepts. It is deliberately as
 * lenient about values as the readers were when these keys lived in
 * `kyc.integration_settings.config` (an empty string is "not set", a shared
 * account may still be the older bare-IBAN string), so the move changes no
 * value and no decision. The rules a SAVE must pass are stricter and stay
 * where they were (gurupay-revenue-settings.ts in the API).
 *
 * Unknown keys are DROPPED when read, never refused: a key a newer release
 * added, or one nobody uses, must not stop payouts or the commission
 * transfer (the readers fail closed only on the keys they use). They are
 * never stored either: a save sends only TREASURY_KEYS (the API refuses any
 * other), and what is stored is the parsed value.
 *
 * The Rust payments service parses `treasury.gurupay` with its own struct
 * (rust/services/payments/src/settings/treasury.rs); both sides run the same
 * sample files in packages/utils/fixtures/settings, so they cannot drift.
 */

import { z } from 'zod';

/** One value per currency, e.g. { EUR: "<GuruPay account uuid>" }. Blank values mean "not set". */
export const currencyMapSchema = z.record(z.string(), z.string());

/**
 * A shared account per currency: { iban, bic }, or the older shape that
 * stored the IBAN alone. `null` is a row the console left empty.
 */
export const sharedAccountEntrySchema = z.union([
  z.string(),
  z.object({ iban: z.string().optional(), bic: z.string().optional() }),
  z.null(),
]);
export const sharedAccountsSchema = z.record(z.string(), sharedAccountEntrySchema);

export const COMMISSION_TRANSFER_MODE_VALUES = ['OFF', 'CALCULATE_ONLY', 'LIVE'] as const;
const TIME_UTC = /^([01]\d|2[0-3]):[0-5]\d$/;
const CENTS = /^\d{1,15}$/;

/** Blank is "not set" (OFF); anything else is one of the three modes. */
const modeSchema = z.string().refine(
  (v) => v.trim() === '' || (COMMISSION_TRANSFER_MODE_VALUES as readonly string[]).includes(v.trim()),
  'OFF, CALCULATE_ONLY, LIVE, or blank',
);
/** Blank is "not set" (23:00). */
const timeSchema = z.string().refine((v) => v.trim() === '' || TIME_UTC.test(v.trim()), 'HH:MM in 24-hour UTC, or blank');
/** Whole cents as a string; a blank row is not an entry. */
const centsMapSchema = z.record(z.string(), z.string().refine((v) => v.trim() === '' || CENTS.test(v.trim()), 'whole cents, or blank'));

/** What every shared-account provider (Bankera, the manual bank, GuruPay's shared mechanic) needs. */
const sharedAccountShape = {
  /** The account every customer of that currency is given. */
  sharedAccounts: sharedAccountsSchema.optional(),
  /** Our name, as the account is registered at the bank. */
  accountHolderName: z.string().optional(),
  /** Prepended to every customer reference. */
  referencePrefix: z.string().optional(),
};

export const gurupayTreasurySchema = z.object({
  ...sharedAccountShape,
  /** The client-money (virtual_parent) account per currency; vIBANs are issued under it. */
  masterAccountUuids: currencyMapSchema.optional(),
  /** Where Norvix commissions are moved to (DEV-127). */
  revenueAccountUuids: currencyMapSchema.optional(),
  /** Filled in from GuruPay when the revenue account is saved. */
  revenueAccountIbans: currencyMapSchema.optional(),
  commissionTransferMode: modeSchema.optional(),
  commissionTransferTimeUtc: timeSchema.optional(),
  commissionTransferMinAmounts: centsMapSchema.optional(),
});

export const sharedAccountTreasurySchema = z.object(sharedAccountShape);

/**
 * IBANQ (IFX), DEV-149. One IBANQ account holds a balance per currency, so the
 * same account id may appear under several currencies; the map is still per
 * currency because which account holds client money is decided per currency.
 * Values are IBANQ account ids (UUIDs from GET /v1/accounts), not IBANs.
 */
export const ibanqTreasurySchema = z.object({
  /** Our client-money (safeguarding) account per currency; customer sub-accounts hang off our client. */
  masterAccountUuids: currencyMapSchema.optional(),
  /** Where Norvix revenue (commissions, FX markup) is moved to. Must not be a client-money account. */
  revenueAccountUuids: currencyMapSchema.optional(),
  /**
   * IBANQ_SHARED (DEV-149): customers are paid into the client-money account
   * above with a reference of their own. Our name as that account is
   * registered, and the prefix of every customer reference.
   */
  accountHolderName: z.string().optional(),
  referencePrefix: z.string().optional(),
});

export type GurupayTreasury = z.infer<typeof gurupayTreasurySchema>;
export type SharedAccountTreasury = z.infer<typeof sharedAccountTreasurySchema>;
export type IbanqTreasury = z.infer<typeof ibanqTreasurySchema>;
/** Any provider's treasury value, read loosely by key. */
export type TreasuryValue = Readonly<Record<string, unknown>>;

/** The providers that hold accounts for us, and the namespace each one's treasury settings live in. */
export const TREASURY_NAMESPACES = {
  GURUPAY: 'treasury.gurupay',
  BANKERA: 'treasury.bankera',
  MANUAL: 'treasury.manual',
  IBANQ: 'treasury.ibanq',
} as const;
export type TreasuryProvider = keyof typeof TREASURY_NAMESPACES;
export type TreasuryNamespace = (typeof TREASURY_NAMESPACES)[TreasuryProvider];

export const TREASURY_PROVIDERS = Object.keys(TREASURY_NAMESPACES) as readonly TreasuryProvider[];

export function isTreasuryProvider(provider: string): provider is TreasuryProvider {
  return Object.prototype.hasOwnProperty.call(TREASURY_NAMESPACES, provider);
}

/** The keys each provider keeps in Treasury; everything else on its row is the connection. */
export const TREASURY_KEYS: Readonly<Record<TreasuryProvider, readonly string[]>> = {
  GURUPAY: Object.keys(gurupayTreasurySchema.shape),
  BANKERA: Object.keys(sharedAccountTreasurySchema.shape),
  MANUAL: Object.keys(sharedAccountTreasurySchema.shape),
  IBANQ: Object.keys(ibanqTreasurySchema.shape),
};

/** Keys of a stored value its namespace does not know (dropped when read; worth a warning). */
export function unknownTreasuryKeys(provider: TreasuryProvider, value: unknown): string[] {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return [];
  return Object.keys(value).filter((key) => !TREASURY_KEYS[provider].includes(key)).sort();
}

/** Is `key` a Treasury setting of this provider (and not part of its connection)? */
export function isTreasuryKey(provider: string, key: string): boolean {
  return isTreasuryProvider(provider) && TREASURY_KEYS[provider].includes(key);
}

const blank = (value: unknown): boolean =>
  value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

/** A Treasury value as the History shows it: maps as "EUR: …, GBP: …", nothing as null. */
export function describeTreasuryValue(value: unknown): string | null {
  if (blank(value)) return null;
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => !blank(v))
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    if (entries.length === 0) return null;
    return entries
      .map(([currency, v]) => `${currency}: ${typeof v === 'object' && v !== null
        ? Object.values(v as Record<string, unknown>).filter((part) => !blank(part)).join(' / ')
        : String(v)}`)
      .join(', ');
  }
  return String(value);
}

/** Every key a save changes, in `keys` order (blank and absent are the same). */
export function treasuryChanges(
  before: TreasuryValue,
  after: TreasuryValue,
  keys: readonly string[],
  labels: Readonly<Record<string, string>>,
): Array<{ key: string; label: string; before: string | null; after: string | null }> {
  return keys
    .map((key) => ({ key, label: labels[key] ?? key, before: describeTreasuryValue(before[key]), after: describeTreasuryValue(after[key]) }))
    .filter((change) => change.before !== change.after);
}
